import { useSuspenseQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import React, { Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { TableSkeleton } from '@eluve/blocks';
import { Stack, Text } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';

import { PatientDetailsImportedNotes } from '../PatientDetailsImportedNotes';
import {
  PatientDocuments,
  patientDocumentsFragment,
} from '../PatientDocuments';

const getPatientDocumentsQuery = graphql(
  `
    query getPatientDocuments($tenantId: uuid!, $patientId: uuid!) {
      patientsByPk(tenantId: $tenantId, id: $patientId) {
        __typename
        id
        ...PatientDocuments
      }
    }
  `,
  [patientDocumentsFragment],
);

const PatientDocumentsSection: React.FC = () => {
  const { tenantId, patientId } = useParams() as {
    tenantId: string;
    patientId: string;
  };

  useSuspenseQuery(getPatientDocumentsQuery, {
    variables: { tenantId, patientId },
  });

  return (
    <PatientDocuments patientId={patientId} tenantId={tenantId} hideHeader />
  );
};

export const PatientDocumentsTab: React.FC = () => {
  return (
    <Stack gap={8}>
      <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
        <Stack gap={3}>
          <Text size="md" weight="semibold">
            Documents
          </Text>
          <Suspense fallback={<TableSkeleton rowCount={5} />}>
            <ErrorBoundary
              fallback={<Text>Failed to load document data.</Text>}
            >
              <PatientDocumentsSection />
            </ErrorBoundary>
          </Suspense>
        </Stack>
      </TenantOrUserFeatureFlaggedComponent>
      <Stack gap={3}>
        <Text size="md" weight="semibold">
          Imported notes
        </Text>
        <ErrorBoundary fallback={<Text>Failed to load imported notes.</Text>}>
          <PatientDetailsImportedNotes />
        </ErrorBoundary>
      </Stack>
    </Stack>
  );
};
